// @flow

import * as React from "react";
import ReactDOM from "react-dom";
import * as mobx from "mobx";
import PT from "prop-types";
import * as o from "rxjs/operators";
import logger from "@vimcar/shared-utils/logger";
import { type Params } from "@vimcar/shared-utils/linkViewModel";
import { type DomainSwitch } from "@vimcar/shared-stores/mobx/DomainSwitch";
import { type Calendar } from "@vimcar/shared-stores/mobx/Calendar";
import { type Bookings } from "@vimcar/shared-stores/mobx/Calendar/Bookings";
import { type Booking } from "@vimcar/shared-stores/mobx/Calendar/Bookings/Booking";
import { type Users } from "@vimcar/shared-stores/mobx/users";

import { O } from "#utils/rx";
import i18n from "#utils/i18n";
import { VimcarBookingLogomarkNeutralInverse } from "#components/VimcarBookingLogomarkNeutralInverse";
import {
  SHOW_WALKTHROUGH_TIMEOUT,
  SETTINGS_KEY,
  BOOKING_MODIFICATION_KEY,
} from "#utils/booking";
import { BREAKPOINT_DESKTOP } from "#utils/ui";
import styles from "./styles.css";

const tp = i18n.prefix("components.dashboard");

const PATH_NEW_BOOKING = "newBooking";
const PATH_MY_BOOKINGS = "myBookings";

type Props = {};

type Context = {
  domain: DomainSwitch<any>,
  calendar: Calendar,
  kiosk: { isKioskMode: boolean },
  permissions: { userCanAccessFleet: boolean },
  persistentStore: { values: mobx.ObservableMap<boolean> },
  users: Users,
  viewport: { is: (string) => boolean },
};

type LayoverStep = {
  position: string,
  selector: string,
  text: string,
  title: string,
};

export type ViewModel = {
  appLayoutRef: { current: null | Object },
  _showSettingsWalkThrough: boolean,
  componentWillUnmount: () => void,
  currentPath: string,
  givenName: string,
  handleClickMyBookings: () => void,
  handleClickNewBooking: () => void,
  hasNoFutureBookings: boolean,
  ownFutureBookings: Array<Booking>,
  settingsWalkThroughSteps: Array<LayoverStep>,
  showLayout: boolean,
  showLinkToAdmin: boolean,
  showOnlySubscibedVehicles: boolean,
  showSettingsWalkThrough: boolean,
};

const mkViewModel = (
  { registerObservable, registerAutorun }: Params,
  _props: Props,
  ctx: Context
): ViewModel => {
  let walkthroughTimeout = null;
  const bookings: null | Bookings = (ctx.calendar.bookings: any);
  if (!bookings) throw new Error("Loading bookings failed");

  const vm: ViewModel = mobx.observable(
    ({
      currentPath: PATH_NEW_BOOKING,

      _showSettingsWalkThrough: false,

      appLayoutRef: React.createRef(),

      get settingsWalkThroughSteps() {
        return [
          {
            position: "bottom-right",
            selector: `.${styles.profileLink}`,
            text: tp("settings-walkthrough-text"),
            title: tp("settings-walkthrough-title"),
          },
        ];
      },

      get showOnlySubscibedVehicles() {
        return (
          !!ctx.domain.currentDomain.settings.map &&
          !ctx.domain.currentDomain.settings.map.booking
            .unsubscribedCarsVisibleBooking
        );
      },

      get ownFutureBookings() {
        return vm.showOnlySubscibedVehicles
          ? bookings.ownAccessible.future
          : bookings.own.future;
      },

      get hasNoFutureBookings() {
        return vm.ownFutureBookings.length === 0;
      },

      get givenName() {
        return ctx.users.optionalSelf.get().name;
      },

      get showLinkToAdmin() {
        return ctx.permissions.userCanAccessFleet;
      },

      get showSettingsWalkThrough() {
        return (
          ctx.viewport.is(BREAKPOINT_DESKTOP) && vm._showSettingsWalkThrough
        );
      },

      get showLayout() {
        return !ctx.kiosk.isKioskMode;
      },

      handleClickNewBooking: () => {
        vm.currentPath = PATH_NEW_BOOKING;
      },

      handleClickMyBookings: () => {
        vm.currentPath = PATH_MY_BOOKINGS;
      },

      componentWillUnmount() {
        window.clearTimeout(walkthroughTimeout);
      },

      componentDidMount() {
        const mediaQueryList = window.matchMedia("(min-width: 768px)");
        mediaQueryList.addEventListener("change", handleMediaQueryChange)
        function handleMediaQueryChange(e) {
          if (e.matches) {
            const svgElement = document.querySelector(
              "main > aside > div > a > svg"
            );
            if (svgElement) {
              const parentElement = svgElement.parentNode;
              if(parentElement){
                parentElement.removeChild(svgElement);
                ReactDOM.render(
                  <VimcarBookingLogomarkNeutralInverse height={24} width={24} />,
                  // $FlowFixMe
                  parentElement
                );
              }
            }
          }
        }

        const svgElement = document.querySelector(
          "main > aside > div > a > svg"
        );


        if (svgElement) {
          const parentElement = svgElement.parentNode;
          if(parentElement){
            parentElement.removeChild(svgElement);
            ReactDOM.render(
              <VimcarBookingLogomarkNeutralInverse height={24} width={24} />,
               // $FlowFixMe
              parentElement
            );
          }
        }
      },
    }: ViewModel)
  );

  const startWalkThrough =
    ctx.viewport.is(BREAKPOINT_DESKTOP) &&
    (ctx.persistentStore.values.get(BOOKING_MODIFICATION_KEY) ||
      (vm.hasNoFutureBookings && vm.currentPath === PATH_MY_BOOKINGS));

  registerAutorun(() => {
    if (startWalkThrough) {
      walkthroughTimeout = window.setTimeout(
        mobx.action(() => {
          if (ctx.persistentStore.values.has(SETTINGS_KEY)) return;
          vm._showSettingsWalkThrough = true;
          ctx.persistentStore.values.set(SETTINGS_KEY, true);
        }),
        SHOW_WALKTHROUGH_TIMEOUT
      );
    }
  });

  registerObservable(
    ctx.calendar.list("future", false).pipe(
      o.catchError((err) => {
        logger.error(err);
        return O.empty();
      })
    )
  );

  return vm;
};

mkViewModel.contextTypes = {
  domain: PT.object,
  calendar: PT.object,
  kiosk: PT.object,
  permissions: PT.object,
  persistentStore: PT.object,
  users: PT.object,
  viewport: PT.object,
};

export default mkViewModel;
